import React, { useState } from 'react';

import moment, { Moment } from 'moment';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Checkbox from '@material-ui/core/Checkbox';

import { addEmployeeGroup } from '../../employee-groups/actions/add-employee-group';
import { updateEmployee } from '../../employees/actions/update-employee';
import { updateEmployeeNotification } from '../../employees/actions/update-employee-notification';
import { userSelector } from '../../reducers/current-user-selectors';
import { LanguageSelector } from '../../widgets/language-selector';

import { EmployeeGroupsBase } from '../../employee-groups/components/employee-groups-base';
import { EmployeeSupervisorsBase } from '../../employees/employee-supervisors-base';
import { UiButton } from '../../ui/ui-button';
import { UiDatePicker } from '../../ui/ui-date-picker';
import { UiDialog } from '../../ui/ui-dialog';

import { employeeName } from '../../employees/utils/employee-name';

const mapStateToProps = (state: any) => ({
  user: userSelector(state),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { application: { orgId } }: InputProps,
) => ({
  actions: {
    addOrgEmployeeGroup: (name: string) => dispatch(addEmployeeGroup(orgId, name)),
    turnOnNotifications: (username: string) =>
      dispatch(updateEmployeeNotification(orgId, username, true)),
    updateEmployee: (employeeId: number, update: Partial<EmployeeUpdateRequest>) =>
      dispatch(updateEmployee(employeeId, update)),
  },
});

type InputProps = {
  application: FullApplicant;
  employee: Employee;
  notifications: Array<EmployeeTextNotification>;
  close: () => void;
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const EmployedDialogContent = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const { application, employee, notifications, user, close, actions } = props;

  const { hireStartDateTime } = application;
  const employeeBirthdate = employee.Contact.Person.birthdate;
  const [receivesNotifications, setReceivesNotifications] = useState<boolean>(false);
  const [languagePreference, setLanguagePreference] = useState<string | null>(
    employee.Contact.Person.languagePreference || null,
  );
  const [supervisorIds, setSupervisorIds] = useState<Array<number>>([]);
  const [hasNoSupervisor, setHasNoSupervisor] = useState<boolean>(false);
  const [employeeGroupIds, setEmployeeGroupIds] = useState<Array<number>>([]);
  const [startDate, setStartDate] = useState<Moment | null>(
    hireStartDateTime ? moment(hireStartDateTime) : moment(),
  );
  const [birthdate, setBirthdate] = useState<Moment | null>(
    employeeBirthdate ? moment(employeeBirthdate) : null,
  );

  const handleRemoveEmployeeGroupId = (removeId: number) =>
    setEmployeeGroupIds(employeeGroupIds.filter((id) => id !== removeId));
  const handleAddEmployeeGroupId = (addId: number) =>
    setEmployeeGroupIds(employeeGroupIds.concat(addId));
  const handleRemoveSupervisorId = (removeId: number) =>
    setSupervisorIds(supervisorIds.filter((id) => id !== removeId));
  const handleNoSupervisor = (noSupervisor: boolean) => {
    if (noSupervisor) setSupervisorIds([]);
    setHasNoSupervisor(noSupervisor);
  };
  const handleAddSupervisorId = (addId: number) => setSupervisorIds(supervisorIds.concat(addId));

  const handleCreateGroup = async (name: string) => {
    const { employeeGroup } = await actions.addOrgEmployeeGroup(name);
    handleAddEmployeeGroupId(employeeGroup.employeeGroupId);
  };

  const handleNotificationsClick = (
    _e: React.ChangeEvent<HTMLInputElement>,
    newReceivesNotifications: boolean,
  ) => {
    setReceivesNotifications(newReceivesNotifications);
  };

  const handleDoneClick = () => {
    if (receivesNotifications && user.username) {
      actions.turnOnNotifications(user.username);
    }
    const Contact = { ...employee.Contact };
    Contact.Person = { ...employee.Contact.Person, languagePreference, birthdate };
    actions.updateEmployee(employee.employeeId, {
      Supervisors: supervisorIds,
      EmployeeGroups: employeeGroupIds,
      hasNoSupervisor,
      Contact,
      startDate,
    });
    close();
  };

  const employeeFullName = employeeName(employee);
  const employeeFirstName = employee.Contact.Person.firstName;
  const userReceivesNotifications = notifications.some(
    (n) => n.username === user.username && n.orgId === employee.orgId,
  );

  return (
    <UiDialog title="Congratulations!" onClose={close}>
      <UiDialog.Content>
        <div className="card--floating card-body">
          <div className="typo-title">
            Now let&apos;s complete {employeeFullName}&apos;s employee profile...
          </div>
          <div className="typo-label u-margin-top">Start Date</div>
          <UiDatePicker autoOk defaultDate={startDate} onChange={setStartDate} />
          <div className="typo-label u-margin-top">Birthdate</div>
          <UiDatePicker autoOk defaultDate={birthdate} onChange={setBirthdate} />
          <div className="u-margin-top">
            <div className="typo-label">Groups</div>
            <div className="typo-subtitle">
              To ensure {employeeFirstName} receives the right announcements
            </div>
            <EmployeeGroupsBase
              orgId={employee.orgId}
              employeeGroupIds={employeeGroupIds}
              onRemove={handleRemoveEmployeeGroupId}
              onAdd={handleAddEmployeeGroupId}
              onCreate={handleCreateGroup}
            />
          </div>
          <div className="u-margin-top">
            <div className="typo-label">Supervisors</div>
            <div className="typo-subtitle">
              To enable messaging between {employeeFirstName} and their manager(s)
            </div>
            <EmployeeSupervisorsBase
              employeeId={employee.employeeId}
              orgId={employee.orgId}
              supervisorIds={supervisorIds}
              hasNoSupervisor={hasNoSupervisor}
              onRemove={handleRemoveSupervisorId}
              onAdd={handleAddSupervisorId}
              onNoSupervisor={handleNoSupervisor}
            />
          </div>
          {!employee.Contact.Person.languagePreference && (
            <div className="u-margin-top">
              <div className="typo-label">Language Preference</div>
              <div className="typo-subtitle">
                To make sure {employeeFirstName} receives messages in their preferred language
              </div>
              <div className="u-maxWidth-350px" style={{ marginTop: -24 }}>
                <LanguageSelector
                  label=""
                  value={languagePreference || ''}
                  onChange={setLanguagePreference}
                />
              </div>
            </div>
          )}
        </div>
        {!userReceivesNotifications && (
          <div className="card--floating card-body u-margin-top-lg">
            <div className="typo-title">We also recommend turning on these employee features:</div>
            {!userReceivesNotifications && (
              <div className="u-margin-top">
                <div className="typo-label">Notifications</div>
                <div className="typo-subtitle">So you know when employees message in</div>
                <div className="u-flex-center">
                  <Checkbox className="u-no-padding" onChange={handleNotificationsClick} />
                  <div className="u-margin-left-sm">
                    Receive notifications for incoming employee messages
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </UiDialog.Content>
      <UiDialog.Actions>
        <UiButton onClick={handleDoneClick}>Done</UiButton>
      </UiDialog.Actions>
    </UiDialog>
  );
});
